import { BadgePercent } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { LazySvg } from '@/Components/custom/lazy-svg'
import { cn } from '@/lib/utils'
import type { Partner, PartnerType } from '@/lib/vip-card'
import type { Bundle } from '@/types'
import { Divider } from './divider'
import { GetCardTab } from './get-card-tab'
import { PartnersTab } from './partners-tab'
import { Reveal, RevealSequence } from './reveal'
import { TelcoTab } from './telco-tab'
import { useVipCardCopy } from './use-vip-card-copy'

/** What the panel writes for this page, as the page row carries it. */
export interface VipCardHeader {
  eyebrow: string
  title: string
  description: string
  benefits: { title: string; description: string; icon: string | null }[]
}

interface VipCardSectionProps {
  partners: Partner[]
  types: PartnerType[]
  offers?: Bundle[]
  header: VipCardHeader
}

type TabKey = 'partners' | 'telco' | 'getCard'

export function VipCardSection({ partners, types, offers = [], header }: VipCardSectionProps) {
  const [tab, setTab] = useState<TabKey>('partners')
  const [hasSwitched, setHasSwitched] = useState(false)

  function open(key: TabKey) {
    setHasSwitched(true)
    setTab(key)
  }
  const copy = useVipCardCopy()

  /*
   * Where the indicator sits, measured against the bar itself. A shared layout
   * animation measures against the viewport instead, and leaving the partners
   * tab the two measurements straddle the old content being removed: the page
   * is briefly short, the scroll clamps, and the indicator starts hundreds of
   * pixels off.
   */
  const bar = useRef<HTMLDivElement>(null)
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null)

  useEffect(() => {
    const track = bar.current
    const active = track?.querySelector<HTMLElement>('[data-active="true"]')

    if (!track || !active) {
      return
    }

    const measure = () => setIndicator({ left: active.offsetLeft, width: active.offsetWidth })

    measure()

    /* The labels change width with the language and the breakpoint. */
    const observer = new ResizeObserver(measure)

    observer.observe(track)

    return () => observer.disconnect()
  }, [tab])

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'partners', label: copy.tabs.partners },
    { key: 'telco', label: copy.tabs.telco },
    { key: 'getCard', label: copy.tabs.getCard },
  ]

  return (
    <RevealSequence>
      <div className="flex w-full flex-col gap-10 lg:gap-14">
        <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-stretch lg:justify-between">
          <Reveal className="flex max-w-[520px] flex-col justify-center gap-6">
            <div className="flex flex-col gap-4">
              <span className="w-fit rounded-full bg-primary/[0.04] px-4 py-2 text-base font-semibold uppercase text-primary dark:bg-primary/[0.22]">
                {header.eyebrow}
              </span>

              <h1 className="text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-[40px] lg:text-[44px]">
                {header.title}
              </h1>
            </div>

            <p className="text-base leading-7 text-muted-foreground sm:text-lg">
              {header.description}
            </p>

            {/* Closes the text column; the card image crosses it. */}
            <span aria-hidden className="mt-auto hidden h-px w-full bg-border lg:block" />
          </Reveal>

          <Reveal order={1} className="w-full max-w-[467px]">
            <img
              src="/images/vip-card/hero-card.png"
              alt=""
              width={467}
              height={300}
              className="w-full transition-transform duration-700 hover:scale-[1.02]"
            />
          </Reveal>
        </div>

        <Reveal order={2} className="flex justify-center">
          <div
            ref={bar}
            className="relative flex gap-3 rounded-full border border-border bg-card p-1"
          >
            {indicator ? (
              <span
                aria-hidden
                data-tab-pill
                style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
                className="absolute inset-y-1 left-0 rounded-full bg-primary transition-[transform,width] duration-300 ease-smooth"
              />
            ) : null}

            {tabs.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                data-active={tab === key}
                onClick={() => open(key)}
                className={cn(
                  'relative h-10 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors duration-300 sm:h-12 sm:px-6 sm:text-base',
                  tab === key
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                  /* Until the indicator has been measured, the active tab paints itself. */
                  tab === key && !indicator && 'bg-primary'
                )}
              >
                <span className="relative z-10">{label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {/* On a small screen they belong to the last tab only; from a tablet up they stay. */}
        <div
          className={cn(
            'flex-col items-stretch gap-6 sm:flex-row sm:items-center sm:justify-center',
            tab === 'getCard' ? 'flex' : 'hidden md:flex'
          )}
        >
          {header.benefits.map((benefit, index) => (
            <Reveal
              key={index}
              order={3}
              index={index}
              className="flex min-w-0 flex-col items-center gap-6 sm:flex-row"
            >
              {/* Between stacked benefits it lies flat; between side-by-side ones it stands. */}
              {index > 0 ? (
                <Divider className="w-full sm:h-16 sm:w-px sm:[--to:to_bottom]" />
              ) : null}

              <div className="flex w-full min-w-0 flex-col items-center gap-4 px-6 text-center sm:w-[250px]">
                {benefit.icon ? (
                  <LazySvg name={benefit.icon} className="size-7 text-foreground" />
                ) : (
                  <BadgePercent className="size-7 text-foreground" />
                )}
                <div className="flex flex-col gap-1">
                  <span className="text-lg font-semibold leading-6 text-foreground">
                    {benefit.title}
                  </span>
                  <span className="text-base leading-6 text-muted-foreground">
                    {benefit.description}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/*
        A CSS entrance rather than AnimatePresence: each panel carries its own
        shared-layout work, and holding one of them mounted to animate it out
        leaves that work half-finished. The first panel has none: its parts take
        their turns in the page's opening sequence instead.
      */}
        <div key={tab} className={hasSwitched ? 'vip-rise' : undefined}>
          {tab === 'partners' ? <PartnersTab partners={partners} types={types} /> : null}
          {tab === 'telco' ? <TelcoTab offers={offers} /> : null}
          {tab === 'getCard' ? <GetCardTab steps={copy.steps} /> : null}
        </div>
      </div>
    </RevealSequence>
  )
}
